import { motion } from "framer-motion";
import { ChevronLeft, Fingerprint, Lock, ShieldCheck, HelpCircle, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import SeoHelmet from "@/components/SeoHelmet";

const CookiePolicy = () => {
  return (
    <div className="min-h-screen bg-[#030303] text-white selection:bg-white/10 selection:text-white">
      <SeoHelmet
        title="Cookie Policy"
        description="Learn how Alvaru uses cookies to improve your experience and protect your privacy. We use only essential cookies for authentication and sessions."
        canonical="https://www.alvaru.com/cookie-policy"
      />
      {/* Background decoration */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-10%] w-[40%] h-[40%] bg-blue-500/5 blur-[120px] rounded-full" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[40%] h-[40%] bg-purple-500/5 blur-[120px] rounded-full" />
      </div>

      <div className="relative max-w-4xl mx-auto px-6 pt-12 pb-24">
        {/* Navigation */}
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-12"
        >
          <Link to="/">
            <Button variant="ghost" className="text-white/60 hover:text-white hover:bg-white/5 group pl-0">
              <ChevronLeft className="mr-2 h-4 w-4 transition-transform group-hover:-translate-x-1" />
              Back to landing page
            </Button>
          </Link>
        </motion.div>

        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-16"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-white/60 text-xs font-medium mb-6">
            <Fingerprint className="w-3 h-3" />
            <span>Privacy & Trust</span>
          </div>
          <h1 className="text-5xl font-bold mb-6 tracking-tight text-white uppercase italic">Cookie Policy</h1>
          <p className="text-white/40 text-lg">
            Last updated: <span className="text-white/60">May 3, 2026</span>
          </p>
        </motion.div>

        {/* Content */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="space-y-12"
        >
          {/* Section 1 */}
          <section className="group">
            <div className="flex items-start gap-4 mb-4">
              <div className="mt-1 p-2 rounded-lg bg-white/5 border border-white/10 group-hover:border-white/20 transition-colors">
                <HelpCircle className="w-5 h-5 text-white/60" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold mb-3">1. What Are Cookies?</h2>
                <p className="text-white/50 leading-relaxed text-lg">
                  Cookies are small text files stored on your device when you visit a website. They help the site function properly and provide information to the site owner about your interaction with the platform.
                </p>
              </div>
            </div>
          </section>

          {/* Section 2 */}
          <section className="group border-t border-white/5 pt-12">
            <div className="flex items-start gap-4 mb-4">
              <div className="mt-1 p-2 rounded-lg bg-white/5 border border-white/10 group-hover:border-white/20 transition-colors">
                <ShieldCheck className="w-5 h-5 text-white/60" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold mb-3">2. How Alvaru Uses Cookies</h2>
                <p className="text-white/50 leading-relaxed text-lg mb-6">
                  We use only essential cookies that are necessary for the website to function. We prioritize your privacy and do not use any third-party tracking or advertising cookies.
                </p>
                <div className="grid gap-4">
                  <div className="p-4 rounded-xl bg-white/5 border border-white/5">
                    <p className="text-white/80 font-medium mb-1 flex items-center gap-2">
                      <Lock className="w-4 h-4 text-primary" />
                      Authentication cookie
                    </p>
                    <p className="text-sm text-white/40 leading-relaxed">Securely keeps you logged in to your Alvaru account across different pages.</p>
                  </div>
                  <div className="p-4 rounded-xl bg-white/5 border border-white/5">
                    <p className="text-white/80 font-medium mb-1 flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-green-500" />
                      Session cookie
                    </p>
                    <p className="text-sm text-white/40 leading-relaxed">Maintains your active session while you are using the CV optimisation dashboard.</p>
                  </div>
                </div>
                <p className="mt-6 text-white/40 text-sm italic">
                  Note: We do not use tracking cookies, advertising cookies, or third-party analytics cookies.
                </p>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section className="group border-t border-white/5 pt-12">
            <div className="flex items-start gap-4 mb-4">
              <div className="mt-1 p-2 rounded-lg bg-white/5 border border-white/10 group-hover:border-white/20 transition-colors">
                <Fingerprint className="w-5 h-5 text-white/60" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold mb-3">3. Managing Cookies</h2>
                <p className="text-white/50 leading-relaxed text-lg">
                  You can control cookies through your browser settings. Most browsers allow you to block or delete cookies. However, please be aware that disabling essential cookies may prevent you from logging in and using the Alvaru platform effectively.
                </p>
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section className="group border-t border-white/5 pt-12 mb-12">
            <div className="p-8 rounded-3xl bg-white/5 border border-white/10 flex flex-col md:flex-row items-center justify-between gap-8">
              <div>
                <h2 className="text-3xl font-bold mb-2 uppercase italic text-white leading-none">Contact Us</h2>
                <p className="text-white/50">Questions about our cookie usage or privacy standards?</p>
              </div>
              <a href="mailto:support@alvaru.com">
                <Button size="lg" className="bg-white text-black hover:bg-white/90 gap-2 h-14 px-8 rounded-2xl font-bold">
                  <Mail className="w-5 h-5" />
                  support@alvaru.com
                </Button>
              </a>
            </div>
          </section>
        </motion.div>

        {/* Footer info */}
        <div className="text-center text-white/20 text-sm border-t border-white/5 pt-12 uppercase tracking-widest">
          &copy; 2026 Alvaru. All rights reserved.
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;
